/* DFN Garden Planner — wizard, scoring, PDF
 * Bootstrapped from sections/garden-planner.liquid via window.DFN_PLANNER config.
 */
(function () {
  'use strict';

  const CFG = window.DFN_PLANNER || {};
  const DATA_URL = CFG.dataUrl;
  const LOGO_URL = CFG.logoUrl;
  const SHOP_DOMAIN = CFG.shopDomain || 'https://dragonflyfarmlanglois.com';
  const CONTACT = CFG.contact || {};

  // ---------- Wizard state ----------
  const STATE = {
    step: 0,
    path: [],            // active step ids derived from projectType
    answers: {
      depth: null,       // 'quick' | 'detailed' — gates the wizard length
      projectType: null, // string id from PROJECT_TYPES
      zone: null,        // integer 3-11 (parsed numeric for plant matching)
      zoneLabel: null,   // string '3a'..'11b' (display + selector value)
      zipInput: '',      // for display only
      sun: null,         // 'full' | 'full_part' | 'part' | 'part_shade' | 'shade'
      space: null,       // 'small' | 'medium' | 'large' | 'container'
      drainage: null,    // 'fast' | 'average' | 'wet' | null (optional)
      water: null,       // 'low' | 'moderate' | 'high'
      maintenance: null, // 'low' | 'moderate' | 'high'
      soil: [],          // multi: ['Sandy','Loam','Clay','Well-drained','Wet-boggy','Rocky-lean']
      slope: null,       // boolean — is the planting site sloped?
      deer: null,        // 'high' | 'low' | 'none'
      coastal: null,     // boolean
      wildlife: [],      // multi
      native: false,     // boolean
      goals: [],         // multi: garden_use values
      bloomColors: [],   // multi
      bloomSeasons: [],  // multi
      vegStyle: null,    // 'raised' | 'in_ground' — only asked for vegetable project
      notes: '',         // freeform customer description (detailed mode only)
    },
    data: null,
    plan: null,
  };

  // ---------- Zip lookup ----------
  // Primary: phzmapi.org (live USDA 2023 data, CloudFront CDN, CORS).
  // Fallback: bundled assets/zip-zones.json (built once, ships with theme).
  // Both cached in memory so retyping the same zip is instant.
  const ZIP_CACHE = {};
  let ZIP_BUNDLE = null;       // {zip: "9b", ...} once loaded
  let ZIP_BUNDLE_PROMISE = null;

  function loadZipBundle() {
    if (ZIP_BUNDLE) return Promise.resolve(ZIP_BUNDLE);
    if (ZIP_BUNDLE_PROMISE) return ZIP_BUNDLE_PROMISE;
    const url = (CFG.dataUrl || '').replace('garden-planner-data.json', 'zip-zones.json');
    ZIP_BUNDLE_PROMISE = fetch(url)
      .then(r => r.ok ? r.json() : null)
      .then(j => { ZIP_BUNDLE = j || {}; return ZIP_BUNDLE; })
      .catch(() => { ZIP_BUNDLE = {}; return ZIP_BUNDLE; });
    return ZIP_BUNDLE_PROMISE;
  }

  function parseZoneStr(zoneStr) {
    const m = (zoneStr || '').match(/^(\d+)/);
    return m ? { zone: parseInt(m[1]), label: zoneStr } : null;
  }

  async function lookupZipZone(zip) {
    if (ZIP_CACHE[zip] !== undefined) return ZIP_CACHE[zip];
    // Try live API first
    try {
      const r = await fetch(`https://phzmapi.org/${zip}.json`);
      if (r.ok) {
        const j = await r.json();
        const result = parseZoneStr(j.zone);
        ZIP_CACHE[zip] = result;
        if (result) return result;
      }
    } catch (e) { /* fall through to bundled */ }

    // Fallback: bundled lookup (only fetched if needed; cached after first load)
    try {
      const bundle = await loadZipBundle();
      const zoneStr = bundle[zip];
      const result = parseZoneStr(zoneStr);
      ZIP_CACHE[zip] = result;
      return result;
    } catch (e) {
      ZIP_CACHE[zip] = null;
      return null;
    }
  }

  // Bloom color choices (must match metafield choice list)
  const BLOOM_COLORS = ['Pink', 'White', 'Purple', 'Red', 'Yellow', 'Cream', 'Orange', 'Blue', 'Brown', 'Green'];

  // ---------- Project Types ----------
  // The project type chosen at Step 1 controls (a) which subsequent wizard steps
  // are shown and (b) which output categories the plant plan is grouped into.
  // `match` is a predicate against a plant record. `cap` is the default count
  // for that bucket; later scaled by space size in computePlan().
  function titleHas(p, re) { return re.test((p.t || '') + ' ' + (p.cn || '')); }

  // Defensive non-edible blocklist — applied to edible + vegetable project
  // categories regardless of the use=Edible/food metafield. The catalog has
  // mistagged ornamentals (azaleas, hydrangeas, ornamental camellias) as
  // edible; we filter them out so customers don't see toxic plants in an
  // "edible" plan. NOTE: this is a code-level safety net; the underlying
  // metafield should be audited and fixed at the Shopify product level too.
  function isLikelyNonEdible(p) {
    const text = ((p.t || '') + ' ' + (p.cn || '') + ' ' + (p.bn || '')).toLowerCase();
    // Camellia sinensis is tea (edible), other camellias are ornamental only
    if (/\bcamellia\b/.test(text) && !/sinensis/.test(text)) return true;
    // Cordyline, "cabbage tree" etc. are ornamentals named for their look
    if (/\b(cabbage tree|cordyline)\b/.test(text)) return true;
    // Anything explicitly labeled ornamental
    if (/\bornamental\b/.test(text)) return true;
    // Known toxic / non-table-crop genera + species
    const blocked = /\b(rhododendron|azalea|hydrangea|wisteria|nerium|oleander|datura|brugmansia|narcissus|daffodil|hyacinth|amaryllis|hippeastrum|delphinium|larkspur|aconitum|monkshood|conium|gloriosa|colchicum|aesculus|horse chestnut|euphorbia|ricinus|castor|abrus|caladium|dieffenbachia|philodendron|alocasia|colocasia|spathiphyllum|monstera|epipremnum|prunus laurocerasus|cherry laurel|taxus|yew|nandina|hedera|english ivy|ligustrum|privet|lily of the valley|convallaria|foxglove|digitalis|nightshade|solanum dulcamara)\b/;
    if (blocked.test(text)) return true;
    return false;
  }

  const PROJECT_TYPES = [
    {
      id: 'pollinator',
      label: 'Pollinator / wildlife border',
      desc: 'A bed full of bee, butterfly, and hummingbird-friendly flowers.',
      steps: ['sun', 'site', 'soil', 'effort', 'pressures', 'wildlife', 'aesthetics'],
      forces: {},
      categories: [
        { id: 'pollinator_trees', label: 'Tree anchors (for larger borders)', desc: 'Small trees that feed pollinators when they bloom. Skip if your space is tight.', cap: 2,
          match: (p) => p.role === 'tree' && p.ht && p.ht[1] <= 25 && Array.isArray(p.wv) && p.wv.some(v => /pollinator|hummingbird|bird forage|larval/i.test(v)) },
        { id: 'tall_accents', label: 'Tall accents', desc: 'Taller perennials and small shrubs that draw the eye.', cap: 4,
          match: (p) => p.role === 'shrub' || (p.role === 'mid' && p.ht && p.ht[1] >= 3) },
        { id: 'mid_bloomers', label: 'Mid-height bloomers', desc: 'The waist-high bee and butterfly magnets.', cap: 8,
          match: (p) => p.role === 'mid' && p.bc && p.bc.length },
        { id: 'front_edgers', label: 'Front edgers', desc: 'Knee-high plants for the front of the bed.', cap: 8,
          match: (p) => p.role === 'border' },
        { id: 'fillers', label: 'Spring bulbs & low fillers', desc: 'Living mulch with early-season interest.', cap: 6,
          match: (p) => p.role === 'ground' },
      ],
    },
    {
      id: 'privacy',
      label: 'Privacy screen / hedge',
      desc: 'Dense planting along a fence or property line.',
      steps: ['sun', 'site', 'soil', 'effort', 'pressures'],
      forces: {},
      categories: [
        { id: 'tall_evergreens', label: 'Tall evergreens', desc: 'Year-round visual screen, your main wall of green.', cap: 6,
          match: (p) => (p.role === 'tree' || p.role === 'shrub') && p.ht && p.ht[1] >= 8 },
        { id: 'mid_screens', label: 'Mid-height screens', desc: 'Fills the 4 to 8 ft band between fence and treetop.', cap: 8,
          match: (p) => p.role === 'shrub' && p.ht && p.ht[1] >= 4 && p.ht[1] < 8 },
        { id: 'screen_filler', label: 'Filler at the base', desc: 'Softens the base of the screen.', cap: 5,
          match: (p) => p.role === 'mid' || (p.role === 'shrub' && p.ht && p.ht[1] < 4) },
      ],
    },
    {
      id: 'shade',
      label: 'Shade garden under a tree',
      desc: 'Plants that thrive without much direct sun.',
      // No 'sun' in steps because we force it to 'part' (dappled / 3-6 hrs).
      // Most "shade garden under tree" sites are open canopy, not deep shade.
      steps: ['site', 'soil', 'effort', 'pressures', 'wildlife', 'aesthetics'],
      forces: { sun: 'part' },
      categories: [
        { id: 'shade_trees', label: 'Shade-makers (trees)', desc: 'Small to mid-size trees to create the canopy. Pick one if you do not already have a tree in place.', cap: 3,
          match: (p) => p.role === 'tree' && p.ht && p.ht[1] <= 35 },
        { id: 'shade_shrubs', label: 'Shade-tolerant shrubs', desc: 'Structure under the tree canopy.', cap: 5,
          match: (p) => p.role === 'shrub' },
        { id: 'ferns', label: 'Ferns', desc: 'Classic shade-garden texture.', cap: 6,
          match: (p) => titleHas(p, /\bfern\b/i) },
        { id: 'shade_perennials', label: 'Shade perennials', desc: 'Hellebore, bleeding heart, foamflower, and friends.', cap: 8,
          match: (p) => (p.role === 'mid' || p.role === 'border') && !titleHas(p, /\bfern\b/i) },
        { id: 'woodland_ground', label: 'Woodland groundcover', desc: 'Living carpet beneath the canopy.', cap: 6,
          match: (p) => p.role === 'ground' },
      ],
    },
    {
      id: 'foundation',
      label: 'Foundation planting along the house',
      desc: 'Beds along your house, walkway, or porch.',
      steps: ['sun', 'site', 'soil', 'effort', 'pressures', 'aesthetics'],
      forces: {},
      categories: [
        { id: 'accent_trees', label: 'Accent trees', desc: 'Small ornamental trees for corners or focal points (Japanese maple, dwarf conifer, etc).', cap: 2,
          match: (p) => p.role === 'tree' && p.ht && p.ht[1] <= 15 },
        { id: 'anchor_shrubs', label: 'Anchor shrubs', desc: 'Year-round structure at the corners.', cap: 6,
          match: (p) => p.role === 'shrub' && p.ht && p.ht[1] <= 6 },
        { id: 'mid_bloomers', label: 'Mid-height bloomers', desc: 'Color and softness against the wall.', cap: 8,
          match: (p) => p.role === 'mid' },
        { id: 'edgers', label: 'Edgers', desc: 'Knee-high plants along the walkway.', cap: 8,
          match: (p) => p.role === 'border' },
        { id: 'ground_filler', label: 'Groundcover filler', desc: 'Tucks in between the larger plants.', cap: 5,
          match: (p) => p.role === 'ground' },
      ],
    },
    {
      id: 'cutting',
      label: 'Cutting garden',
      desc: 'A bed grown for cut flowers and arrangements.',
      steps: ['sun', 'site', 'soil', 'effort', 'pressures', 'aesthetics'],
      forces: {},
      categories: [
        { id: 'tall_cuts', label: 'Tall cuts', desc: 'Statement stems for the back of arrangements.', cap: 6,
          match: (p) => Array.isArray(p.use) && p.use.includes('Cut flowers') && p.ht && p.ht[1] >= 3 },
        { id: 'mid_cuts', label: 'Mid-height cuts', desc: 'The middle of the bouquet, lasting workhorses.', cap: 8,
          match: (p) => Array.isArray(p.use) && p.use.includes('Cut flowers') && p.ht && p.ht[1] < 3 && p.ht[1] >= 1.5 },
        { id: 'foliage_filler', label: 'Foliage & filler', desc: 'Texture and greens for arrangements.', cap: 6,
          match: (p) => p.role === 'border' && p.fc && p.fc.length },
      ],
    },
    {
      id: 'edible',
      label: 'Edible landscape',
      desc: 'Food-producing plants that also look good in the landscape.',
      steps: ['sun', 'site', 'soil', 'effort', 'pressures'],
      forces: {},
      categories: [
        { id: 'fruit_trees', label: 'Fruit trees', desc: 'Standard and dwarf fruit trees.', cap: 5,
          match: (p) => p.role === 'tree' && Array.isArray(p.use) && p.use.includes('Edible/food') && !isLikelyNonEdible(p) },
        { id: 'berry_shrubs', label: 'Berry shrubs', desc: 'Blueberries, currants, and friends.', cap: 8,
          match: (p) => p.role === 'shrub' && Array.isArray(p.use) && p.use.includes('Edible/food') && !isLikelyNonEdible(p) },
        { id: 'herbs_perennials', label: 'Herbs & edible perennials', desc: 'Repeat harvests, year after year.', cap: 8,
          match: (p) => (p.role === 'mid' || p.role === 'border') && Array.isArray(p.use) && p.use.includes('Edible/food') && !isLikelyNonEdible(p) },
        { id: 'edible_ground', label: 'Edible groundcover', desc: 'Ground cover that earns its keep, like strawberries.', cap: 5,
          match: (p) => p.role === 'ground' && Array.isArray(p.use) && p.use.includes('Edible/food') && !isLikelyNonEdible(p) },
      ],
    },
    {
      id: 'meadow',
      label: 'Meadow / native prairie patch',
      desc: 'A loose, wild-feeling planting of grasses and flowering forbs.',
      steps: ['sun', 'site', 'soil', 'effort', 'pressures', 'wildlife', 'aesthetics'],
      forces: {},
      categories: [
        { id: 'grasses', label: 'Grasses & sedges', desc: 'Movement, structure, and seed heads.', cap: 6,
          match: (p) => titleHas(p, /\b(grass|sedge|rush|hairgrass|fescue|wildrye|oatgrass|wheatgrass|reedgrass)\b/i) },
        { id: 'forbs', label: 'Wildflowers & forbs', desc: 'The flowering body of the meadow.', cap: 10,
          match: (p) => (p.role === 'mid' || p.role === 'border') && p.bc && p.bc.length },
        { id: 'meadow_ground', label: 'Low fillers', desc: 'Knits the meadow together at ground level.', cap: 6,
          match: (p) => p.role === 'ground' },
      ],
    },
    {
      id: 'vegetable',
      label: 'Vegetable garden',
      desc: 'Raised beds or in-ground rows of veggies, herbs, berries, and companion plants.',
      // veg_site combines size + raised/in-ground, so no separate site step.
      // No wildlife/aesthetics — companion plants handle pollinators, no
      // bloom-color preferences for crops.
      steps: ['sun', 'veg_site', 'effort', 'pressures'],
      forces: {},
      categories: [
        { id: 'veggie_starts', label: 'Annual veggie starts', desc: 'Tomatoes, peppers, brassicas, and other annual crops you grow from starts each spring.', cap: 6,
          match: (p) => /\b(tomato|pepper|cabbage|kale|spinach|broccoli|cauliflower|brussels|chard|lettuce|arugula|squash|cucumber|melon|pumpkin|zucchini|bean|pea|corn|eggplant|onion|leek|garlic|carrot|beet|radish|turnip|fennel|celery|asparagus|artichoke)\b/i.test((p.t || '') + ' ' + (p.cn || '')) && Array.isArray(p.use) && p.use.includes('Edible/food') && !isLikelyNonEdible(p) },
        { id: 'herbs', label: 'Culinary herbs', desc: 'Basil, parsley, thyme, sage, oregano, the kitchen staples.', cap: 8,
          match: (p) => /\b(basil|parsley|cilantro|coriander|oregano|thyme|sage|rosemary|mint|chive|dill|tarragon|chamomile|stevia|marjoram|savory|lovage|hyssop|lemon balm|bay laurel)\b/i.test((p.t || '') + ' ' + (p.cn || '')) && !isLikelyNonEdible(p) },
        { id: 'companion_flowers', label: 'Companion flowers', desc: 'Pest deterrents and pollinator magnets to interplant with your crops.', cap: 6,
          match: (p) => /\b(marigold|calendula|nasturtium|alyssum|borage|cosmos|zinnia|monarda|tagetes|tropaeolum|bee balm|yarrow|achillea)\b/i.test((p.t || '') + ' ' + (p.cn || '')) },
        { id: 'berries_fruit', label: 'Berries & fruit', desc: 'Blueberries, currants, gooseberries, and other perennial fruit.', cap: 6,
          match: (p) => p.role === 'shrub' && Array.isArray(p.use) && p.use.includes('Edible/food') && !isLikelyNonEdible(p) },
        { id: 'perennial_edibles', label: 'Perennial edibles', desc: 'Plant once, harvest for years. Asparagus, rhubarb, sorrel, perennial alliums.', cap: 5,
          match: (p) => Array.isArray(p.use) && p.use.includes('Edible/food') && (p.role === 'mid' || p.role === 'border') && !isLikelyNonEdible(p) },
      ],
    },
    {
      id: 'whole_yard',
      label: 'Whole yard redesign',
      desc: 'A full landscape: trees, shrubs, perennials, and groundcover.',
      steps: ['sun', 'site', 'soil', 'effort', 'pressures', 'wildlife', 'aesthetics'],
      forces: {},
      categories: [
        { id: 'tree', label: 'Trees', desc: "Anchors and shade-makers, your garden's skyline.", cap: 5,
          match: (p) => p.role === 'tree' },
        { id: 'shrub', label: 'Shrubs', desc: 'Structure plants. Privacy, hedge, four-season backbone.', cap: 8,
          match: (p) => p.role === 'shrub' },
        { id: 'mid', label: 'Mid-layer plants', desc: 'The waist-high middle that ties your garden together.', cap: 10,
          match: (p) => p.role === 'mid' },
        { id: 'border', label: 'Borders & accents', desc: 'Knee-high color and softness along edges and paths.', cap: 10,
          match: (p) => p.role === 'border' },
        { id: 'ground', label: 'Groundcovers', desc: 'Living mulch, covers soil and suppresses weeds.', cap: 6,
          match: (p) => p.role === 'ground' },
      ],
    },
  ];

  function getProjectType(id) {
    return PROJECT_TYPES.find(p => p.id === id) || null;
  }

  // ---------- Steps ----------
  // Each step has:
  //   render:   function returning HTML
  //   label:    short progress label (unused visually, kept for debug)
  //   validate: optional function returning true | error string
  //   mode:     'always' (shown in both Quick + Detailed) or 'detailed' (Detailed only)
  //   requires: optional predicate, step shown only when it returns true
  const STEPS_BY_ID = {
    welcome: {
      render: renderWelcome, label: 'Start', mode: 'always',
    },
    depth: {
      render: renderDepth, label: 'Depth', mode: 'always',
      validate: () => STATE.answers.depth !== null || 'Pick how detailed you want to go.',
    },
    project: {
      render: renderProject, label: 'Project', mode: 'always',
      validate: () => STATE.answers.projectType !== null || 'Pick a project type to get started.',
    },
    zone: {
      render: renderZone, label: 'Where you live', mode: 'always',
      validate: () => STATE.answers.zone !== null || 'Please tell us your zip or USDA zone.',
    },
    sun: {
      render: renderSun, label: 'Sun', mode: 'always',
      validate: () => STATE.answers.sun !== null || 'Pick the sun your space gets.',
    },
    site: {
      render: renderSite, label: 'Site', mode: 'always',
      // In Quick mode we only require space; Detailed asks slope too.
      validate: () => {
        if (STATE.answers.space === null) return 'Pick a space size.';
        if (STATE.answers.depth === 'detailed' && STATE.answers.slope === null) return 'Tell us if your site is on a slope.';
        return true;
      },
    },
    effort: {
      render: renderEffort, label: 'Watering & care', mode: 'always',
      validate: () => {
        if (STATE.answers.water === null) return 'Pick a watering style.';
        const isVeg = STATE.answers.projectType === 'vegetable';
        const showMaint = isVeg || STATE.answers.depth === 'detailed';
        if (showMaint && STATE.answers.maintenance === null) return 'Pick a maintenance level.';
        return true;
      },
    },
    soil: {
      render: renderSoil, label: 'Soil', mode: 'detailed',
    },
    pressures: {
      render: renderPressures, label: 'Pressures', mode: 'detailed',
      validate: () => (STATE.answers.deer !== null && STATE.answers.coastal !== null) || 'A couple more checks first.',
    },
    wildlife: {
      render: renderWildlife, label: 'Wildlife', mode: 'detailed',
    },
    goals: {
      render: renderGoals, label: 'Goals', mode: 'detailed',
    },
    aesthetics: {
      render: renderAesthetics, label: 'Look', mode: 'detailed',
    },
    veg_site: {
      render: renderVegSite, label: 'Garden setup', mode: 'always',
      validate: () => {
        if (STATE.answers.space === null) return 'Pick a garden size.';
        if (STATE.answers.vegStyle === null) return 'Pick raised, in-ground, or containers.';
        return true;
      },
    },
    results: {
      render: renderResults, label: 'Your plan', mode: 'always',
    },
  };

  // The wizard path is composed: [universal prefix] + [per-project middle]
  // + [universal suffix]. Each project type declares its own middle so the
  // wizard adapts to what's actually relevant. Quick mode then strips the
  // detailed-only steps (those with mode: 'detailed').
  const PATH_PREFIX = ['welcome', 'depth', 'project', 'zone'];
  const PATH_SUFFIX = ['results'];
  // Fallback middle if no project picked yet (used for the early wizard render
  // before recomputePath fires post-project-step)
  const DEFAULT_MIDDLE = ['sun', 'site', 'soil', 'effort', 'pressures', 'wildlife', 'aesthetics'];

  function recomputePath() {
    const pt = getProjectType(STATE.answers.projectType);
    const middle = pt?.steps || DEFAULT_MIDDLE;
    const isQuick = STATE.answers.depth === 'quick';
    const candidates = [...PATH_PREFIX, ...middle, ...PATH_SUFFIX];
    STATE.path = candidates.filter(id => {
      const s = STEPS_BY_ID[id];
      if (!s) return false;
      if (s.requires && !s.requires()) return false;
      if (isQuick && s.mode === 'detailed') return false;
      return true;
    });
    if (pt?.forces) Object.assign(STATE.answers, pt.forces);
  }

  // ---------- Boot ----------
  function boot() {
    const root = document.getElementById('garden-planner-app');
    if (!root) return;
    STATE.root = root;
    recomputePath(); // initial path = full path (no project picked yet)
    render();
  }

  function render(opts) {
    opts = opts || {};
    const stepId = STATE.path[STATE.step];
    const step = STEPS_BY_ID[stepId];
    const showProgress = STATE.step > 0 && STATE.step < STATE.path.length - 1;
    const stepChanged = STATE._lastRenderedStep !== STATE.step;
    const dotIds = STATE.path.slice(1, -1);
    STATE.root.innerHTML = `
      ${showProgress ? `
        <div class="gp-progress" aria-hidden="true">
          ${dotIds.map((id, i) => `
            <span class="gp-progress__dot ${STATE.step >= i + 1 ? 'is-done' : ''} ${STATE.step === i + 1 ? 'is-active' : ''}"></span>
          `).join('')}
        </div>
      ` : ''}
      <div class="gp-step gp-step--${stepId}">${step.render()}</div>
    `;
    bindEvents();
    // On step change, scroll to the very top of the page so the H1 is visible.
    // Behaves like a normal page load — no in-page jumping past the header.
    if (stepChanged && !opts.skipScroll) {
      window.scrollTo(0, 0);
    }
    STATE._lastRenderedStep = STATE.step;
  }

  function bindEvents() {
    STATE.root.querySelectorAll('[data-action]').forEach(el => {
      el.addEventListener('click', e => {
        const action = el.dataset.action;
        if (action === 'next') goNext();
        else if (action === 'back') goBack();
        else if (action === 'restart') restart();
        else if (action === 'pdf') generatePDF();
      });
    });

    STATE.root.querySelectorAll('[data-set]').forEach(el => {
      el.addEventListener('click', e => {
        const [key, val] = el.dataset.set.split(':');
        let v = val;
        if (v === 'true') v = true;
        else if (v === 'false') v = false;
        else if (!isNaN(parseInt(v))) v = parseInt(v);
        STATE.answers[key] = v;
        // In-place visual update: clear active siblings sharing this key, mark this one
        const peers = STATE.root.querySelectorAll(`[data-set^="${key}:"]`);
        peers.forEach(p => p.classList.remove('is-active'));
        el.classList.add('is-active');
      });
    });

    STATE.root.querySelectorAll('[data-toggle]').forEach(el => {
      el.addEventListener('click', e => {
        const [key, val] = el.dataset.toggle.split(':');
        const arr = STATE.answers[key];
        const i = arr.indexOf(val);
        if (i >= 0) {
          arr.splice(i, 1);
          el.classList.remove('is-active');
        } else {
          arr.push(val);
          el.classList.add('is-active');
        }
      });
    });

    const zip = STATE.root.querySelector('#gp-zip');
    if (zip) {
      let zipDebounce;
      zip.addEventListener('input', e => {
        const v = e.target.value.replace(/\D/g, '').slice(0, 5);
        e.target.value = v;
        STATE.answers.zipInput = v;
        const fb = STATE.root.querySelector('#gp-zone-feedback');

        if (v.length < 5) {
          if (fb) fb.textContent = v.length > 0 ? '' : '';
          return;
        }

        if (fb) fb.textContent = 'Looking up your zone…';
        clearTimeout(zipDebounce);
        zipDebounce = setTimeout(async () => {
          const result = await lookupZipZone(v);
          // Make sure user hasn't typed something else in the meantime
          if (STATE.answers.zipInput !== v) return;
          if (result) {
            STATE.answers.zone = result.zone;
            STATE.answers.zoneLabel = result.label;
            const sel = STATE.root.querySelector('#gp-zone-select');
            if (sel) sel.value = result.label;
            if (fb) fb.textContent = `Zone ${result.label} — got it. (Change below if you know yours.)`;
          } else {
            if (fb) fb.textContent = `We couldn't find that ZIP — please pick your zone below.`;
          }
        }, 250);
      });
    }
    const zoneSel = STATE.root.querySelector('#gp-zone-select');
    if (zoneSel) {
      zoneSel.addEventListener('change', e => {
        const v = e.target.value;
        STATE.answers.zone = parseInt(v) || null;
        STATE.answers.zoneLabel = v || null;
      });
    }
    const nativeChk = STATE.root.querySelector('#gp-native');
    if (nativeChk) {
      nativeChk.addEventListener('change', e => {
        STATE.answers.native = e.target.checked;
      });
    }

    const notesEl = STATE.root.querySelector('#gp-notes');
    if (notesEl) {
      notesEl.addEventListener('input', e => {
        STATE.answers.notes = e.target.value;
      });
    }
  }

  function goNext() {
    const stepId = STATE.path[STATE.step];
    const step = STEPS_BY_ID[stepId];
    if (step.validate) {
      const ok = step.validate();
      if (ok !== true) {
        alert(ok);
        return;
      }
    }
    // Steps that change which other steps appear → recompute the path before
    // stepping forward so skipped steps disappear and forced answers apply.
    if (stepId === 'project' || stepId === 'depth') recomputePath();

    if (STATE.step === STATE.path.length - 2) {
      // about to enter results — load data and compute
      loadData().then(() => {
        STATE.plan = computePlan(STATE.answers, STATE.data);
        STATE.step++;
        render();
      });
      return;
    }
    STATE.step++;
    render();
  }

  function goBack() {
    if (STATE.step > 0) {
      STATE.step--;
      render();
    }
  }

  function restart() {
    STATE.step = 0;
    Object.assign(STATE.answers, {
      depth: null, projectType: null,
      zone: null, zoneLabel: null, zipInput: '',
      sun: null, space: null, drainage: null,
      water: null, maintenance: null,
      soil: [], slope: null,
      deer: null, coastal: null,
      wildlife: [], native: false, goals: [],
      bloomColors: [], bloomSeasons: [],
      vegStyle: null, notes: '',
    });
    recomputePath();
    render();
  }

  // ---------- Data ----------
  function loadData() {
    if (STATE.data) return Promise.resolve(STATE.data);
    STATE.root.innerHTML = `<div class="gp-loading">Pulling together your plant matches…</div>`;
    return fetch(DATA_URL)
      .then(r => r.json())
      .then(d => { STATE.data = d; return d; });
  }

  // ---------- Step renderers ----------
  function btn(label, action, kind) {
    return `<button class="gp-btn gp-btn--${kind || 'primary'}" data-action="${action}">${label}</button>`;
  }

  function navRow() {
    const isLast = STATE.path[STATE.step + 1] === 'results';
    const nextLabel = isLast ? 'See my plan →' : 'Next →';
    return `
      <div class="gp-nav">
        ${STATE.step > 1 ? `<button class="gp-btn gp-btn--ghost" data-action="back">← Back</button>` : '<span></span>'}
        <button class="gp-btn gp-btn--primary" data-action="next">${nextLabel}</button>
      </div>
    `;
  }

  // Inline "Anything else?" notes textarea — rendered at the bottom of whichever
  // step is the actual last step before results, in detailed mode only.
  function notesSubblockIfLast() {
    const isLast = STATE.path[STATE.step + 1] === 'results';
    if (!isLast || STATE.answers.depth !== 'detailed') return '';
    return `
      <div class="gp-subblock">
        <div class="gp-sublabel">Anything else? <span class="gp-optional">(optional)</span></div>
        <p class="gp-help" style="margin-top:-6px;margin-bottom:10px">Describe your space or your dreams for it. We'll include this in your PDF as your notes so you have it captured in one place.</p>
        <textarea id="gp-notes" class="gp-textarea" rows="5" placeholder="Example: My front yard slopes south toward the road. Mature Doug fir on the west side throws shade until noon. Want a pollinator-friendly bed I can see from the porch...">${escapeHtml(STATE.answers.notes || '')}</textarea>
      </div>
    `;
  }

  function renderWelcome() {
    return `
      <div class="gp-hero">
        <p class="gp-hero__sub">Tell us what you're trying to grow and we'll match plants from our 7,000+ nursery selection to your project, your site, and the look you love. You'll leave with a printable plant list ready to bring in next time you visit us.</p>
        <p class="gp-hero__time">Takes about 2 minutes.</p>
        ${btn('Start your plan', 'next', 'primary')}
      </div>
    `;
  }

  function renderDepth() {
    const sel = STATE.answers.depth;
    return `
      <h3 class="gp-q">Quick plan or detailed plan?</h3>
      <p class="gp-help">Both pull from our 7,000+ plant catalog. Quick gives you a solid starter plan in under a minute. Detailed asks more about your site, wildlife wishes, color preferences, and lets you describe your space in your own words.</p>
      <div class="gp-cards">
        <button class="gp-card ${sel === 'quick' ? 'is-active' : ''}" data-set="depth:quick">
          <div class="gp-card__t">Quick (5 questions, ~1 min)</div>
          <div class="gp-card__d">Project type, zone, sun, space, water. Get a fast plant list to take into the nursery.</div>
        </button>
        <button class="gp-card ${sel === 'detailed' ? 'is-active' : ''}" data-set="depth:detailed">
          <div class="gp-card__t">Detailed (12 questions, ~3 min)</div>
          <div class="gp-card__d">Adds soil, deer pressure, coastal exposure, wildlife goals, color preferences, notes. Your PDF includes a bloom calendar, phased planting guide, plants to avoid, and sourcing tips.</div>
        </button>
      </div>
      ${navRow()}
    `;
  }

  function renderProject() {
    const sel = STATE.answers.projectType;
    return `
      <h3 class="gp-q">What are you trying to plant?</h3>
      <p class="gp-help">Pick the project that best matches what you're working on. We'll tailor the rest of the questions and your plant list to fit.</p>
      <div class="gp-cards">
        ${PROJECT_TYPES.map(pt => `
          <button class="gp-card ${sel === pt.id ? 'is-active' : ''}" data-set="projectType:${pt.id}">
            <div class="gp-card__t">${pt.label}</div>
            <div class="gp-card__d">${pt.desc}</div>
          </button>
        `).join('')}
      </div>
      ${navRow()}
    `;
  }

  function renderZone() {
    const z = STATE.answers.zone;
    const zLabel = STATE.answers.zoneLabel || (z ? String(z) : '');
    // USDA hardiness zones 3a–11b cover continental US + Hawaii. Add 1a–2b/12a–13b
    // if needed in the future.
    const zoneOpts = [];
    for (let n = 3; n <= 11; n++) {
      zoneOpts.push(`${n}a`);
      zoneOpts.push(`${n}b`);
    }
    return `
      <h3 class="gp-q">First, where do you live?</h3>
      <p class="gp-help">USDA hardiness zones tell us which plants survive your winters. Type your zip and we'll look it up, or pick your zone if you already know it.</p>
      <div class="gp-zone-row">
        <label class="gp-field">
          <span class="gp-field__label">ZIP code</span>
          <input id="gp-zip" type="text" inputmode="numeric" placeholder="e.g. 97450" value="${STATE.answers.zipInput}" />
        </label>
        <span class="gp-or">or</span>
        <label class="gp-field">
          <span class="gp-field__label">USDA zone</span>
          <select id="gp-zone-select">
            <option value="">Pick a zone</option>
            ${zoneOpts.map(z => `<option value="${z}" ${zLabel === z ? 'selected' : ''}>Zone ${z}</option>`).join('')}
          </select>
        </label>
      </div>
      <p class="gp-feedback" id="gp-zone-feedback">${zLabel ? `Zone ${zLabel} — got it.` : ''}</p>
      ${navRow()}
    `;
  }

  function renderSun() {
    const opts = [
      { v: 'full', t: 'Full sun', d: '6+ hours of direct sun' },
      { v: 'full_part', t: 'Mostly sun', d: 'A few hours of afternoon shade is fine' },
      { v: 'part', t: 'Part shade', d: '3-6 hours of sun, often morning' },
      { v: 'part_shade', t: 'Mostly shade', d: 'Dappled or filtered light most of the day' },
      { v: 'shade', t: 'Full shade', d: 'Less than 3 hours of direct sun' },
    ];
    return `
      <h3 class="gp-q">How much sun does your space get?</h3>
      <p class="gp-help">If you're not sure, watch where the sun lands at noon and at 3pm.</p>
      <div class="gp-cards">
        ${opts.map(o => `
          <button class="gp-card ${STATE.answers.sun === o.v ? 'is-active' : ''}" data-set="sun:${o.v}">
            <div class="gp-card__t">${o.t}</div>
            <div class="gp-card__d">${o.d}</div>
          </button>
        `).join('')}
      </div>
      ${navRow()}
    `;
  }

  function renderSite() {
    const a = STATE.answers;
    const isQuick = a.depth === 'quick';
    const sizes = [
      { v: 'container', t: 'Pots & containers', d: 'Patio, balcony, or planters only' },
      { v: 'small', t: 'Small bed', d: 'Under 100 sq ft — a corner, a strip' },
      { v: 'medium', t: 'Medium garden', d: '100-500 sq ft — a typical front bed' },
      { v: 'large', t: 'Large landscape', d: '500+ sq ft — a yard, a hillside, an acre' },
    ];
    return `
      <h3 class="gp-q">${isQuick ? 'How much room do you have?' : 'Tell us about the planting spot.'}</h3>
      <p class="gp-help">${isQuick ? 'Pick the closest match. We use this to steer big trees and shrubs in or out of your plan.' : "A few quick questions about the actual ground you're planting in. We'll use this to steer plants in or out of your plan."}</p>

      <div class="gp-subblock">
        ${isQuick ? '' : '<div class="gp-sublabel">How much room do you have?</div>'}
        <div class="gp-cards">
          ${sizes.map(o => `
            <button class="gp-card ${a.space === o.v ? 'is-active' : ''}" data-set="space:${o.v}">
              <div class="gp-card__t">${o.t}</div>
              <div class="gp-card__d">${o.d}</div>
            </button>
          `).join('')}
        </div>
      </div>

      ${isQuick ? '' : `
      <div class="gp-subblock">
        <div class="gp-sublabel">Is your planting site on a slope?</div>
        <div class="gp-pills">
          <button class="gp-pill ${a.slope === true ? 'is-active' : ''}" data-set="slope:true">Yes, sloped or hillside</button>
          <button class="gp-pill ${a.slope === false ? 'is-active' : ''}" data-set="slope:false">No, flat ground</button>
        </div>
      </div>

      <div class="gp-subblock">
        <div class="gp-sublabel">After heavy rain, the spot tends to: <span class="gp-optional">(optional)</span></div>
        <div class="gp-pills">
          <button class="gp-pill ${a.drainage === 'fast' ? 'is-active' : ''}" data-set="drainage:fast">Drain fast, never puddles</button>
          <button class="gp-pill ${a.drainage === 'average' ? 'is-active' : ''}" data-set="drainage:average">Average, drains in a day or so</button>
          <button class="gp-pill ${a.drainage === 'wet' ? 'is-active' : ''}" data-set="drainage:wet">Stay wet, puddles for days</button>
        </div>
      </div>
      `}

      ${navRow()}
    `;
  }

  function renderVegSite() {
    const a = STATE.answers;
    const sizes = [
      { v: 'container', t: 'Containers / pots / grow bags', d: '1 to 5 containers on a patio, balcony, or porch.' },
      { v: 'small', t: 'One small bed', d: 'A single 4×4 or 4×8 raised bed, or a 10×10 in-ground patch.' },
      { v: 'medium', t: 'A few beds', d: '2 to 3 raised beds, or a 100 to 300 sq ft in-ground garden.' },
      { v: 'large', t: 'Larger garden', d: '4+ raised beds, or 300+ sq ft in-ground.' },
    ];
    const styles = [
      { v: 'raised', t: 'Raised beds or planters', d: 'Wood, metal, or fabric beds filled with bagged soil mix.' },
      { v: 'in_ground', t: 'In-ground beds', d: 'Existing soil amended with compost, dug or no-till.' },
      { v: 'container', t: 'Containers only', d: 'Pots, grow bags, or window boxes on a patio or balcony.' },
    ];
    return `
      <h3 class="gp-q">Tell us about your vegetable garden setup.</h3>
      <p class="gp-help">Both questions shape your plan: size determines how many of each crop we recommend, style determines the soil prep guide.</p>

      <div class="gp-subblock">
        <div class="gp-sublabel">How big is your veg garden?</div>
        <div class="gp-cards">
          ${sizes.map(o => `
            <button class="gp-card ${a.space === o.v ? 'is-active' : ''}" data-set="space:${o.v}">
              <div class="gp-card__t">${o.t}</div>
              <div class="gp-card__d">${o.d}</div>
            </button>
          `).join('')}
        </div>
      </div>

      <div class="gp-subblock">
        <div class="gp-sublabel">How is it built?</div>
        <div class="gp-cards">
          ${styles.map(o => `
            <button class="gp-card ${a.vegStyle === o.v ? 'is-active' : ''}" data-set="vegStyle:${o.v}">
              <div class="gp-card__t">${o.t}</div>
              <div class="gp-card__d">${o.d}</div>
            </button>
          `).join('')}
        </div>
      </div>

      ${navRow()}
    `;
  }


  function renderEffort() {
    const a = STATE.answers;
    const isQuick = a.depth === 'quick';
    const isVeg = a.projectType === 'vegetable';

    const water = isVeg ? [
      { v: 'high', t: 'Drip irrigation set up', d: 'Watering on a timer. The easiest path for a productive veg garden.' },
      { v: 'moderate', t: 'I will hand-water regularly', d: 'Realistic for a small to medium garden. Plan for daily attention in summer.' },
      { v: 'low', t: 'I want easygoing edibles only', d: 'Filters to drought-tolerant herbs and perennial edibles. Skip thirsty annuals like lettuce and tomato.' },
    ] : [
      { v: 'low', t: 'Hands-off', d: 'I want plants that get by on rain once established' },
      { v: 'moderate', t: 'Steady', d: 'I can water weekly in dry stretches' },
      { v: 'high', t: 'Generous', d: 'I have time to water often or have irrigation' },
    ];

    const maint = isVeg ? [
      { v: 'low', t: 'Keep it simple', d: 'Lettuce, kale, bush beans, sturdy herbs. Minimal pruning or trellising.' },
      { v: 'moderate', t: 'Most veggies', d: 'Tomatoes, peppers, zucchini. Regular harvesting and weeding.' },
      { v: 'high', t: 'I love the work', d: 'Heirloom tomatoes, succession planting, espaliered fruit. Bring it on.' },
    ] : [
      { v: 'low', t: 'Plant and forget', d: 'Minimal pruning, deadheading, or fussing' },
      { v: 'moderate', t: 'A few times a season', d: 'I\'ll prune, deadhead, and tidy as needed' },
      { v: 'high', t: 'I love puttering', d: 'Pruning, deadheading, dividing, bring it on' },
    ];

    const heading = isVeg
      ? (isQuick ? 'How will you water your veg garden?' : 'Watering and upkeep style?')
      : (isQuick ? 'How much will you water?' : 'How much time will you spend in the garden?');
    const helpCopy = isVeg
      ? 'Vegetable gardens always need regular attention. These help us tune the recommendations to your time and equipment.'
      : "Be honest with yourself, we'll steer you toward plants that match the energy you actually have.";

    return `
      <h3 class="gp-q">${heading}</h3>
      <p class="gp-help">${helpCopy}</p>

      <div class="gp-subblock">
        ${isQuick && !isVeg ? '' : `<div class="gp-sublabel">${isVeg ? 'Watering setup' : 'Watering'}</div>`}
        <div class="gp-cards">
          ${water.map(o => `
            <button class="gp-card ${a.water === o.v ? 'is-active' : ''}" data-set="water:${o.v}">
              <div class="gp-card__t">${o.t}</div>
              <div class="gp-card__d">${o.d}</div>
            </button>
          `).join('')}
        </div>
      </div>

      ${(isQuick && !isVeg) ? '' : `
      <div class="gp-subblock">
        <div class="gp-sublabel">${isVeg ? 'How much fuss do you want to take on?' : 'General garden upkeep'}</div>
        <div class="gp-cards">
          ${maint.map(o => `
            <button class="gp-card ${a.maintenance === o.v ? 'is-active' : ''}" data-set="maintenance:${o.v}">
              <div class="gp-card__t">${o.t}</div>
              <div class="gp-card__d">${o.d}</div>
            </button>
          `).join('')}
        </div>
      </div>
      `}

      ${navRow()}
    `;
  }

  function renderSoil() {
    const a = STATE.answers;
    const opts = [
      { v: 'Loam', t: 'Loamy', d: 'Crumbly, dark, holds moisture well — the gardener\'s dream' },
      { v: 'Sandy', t: 'Sandy', d: 'Drains fast, dries out quickly between waterings' },
      { v: 'Clay', t: 'Clay', d: 'Heavy, holds water (sometimes too well), hard when dry' },
      { v: 'Rocky-lean', t: 'Rocky / lean', d: 'Gravelly, low-nutrient — good for drought-tough plants' },
      { v: 'Wet-boggy', t: 'Wet / boggy', d: 'Stays moist or wet most of the year — rain garden territory' },
      { v: 'Well-drained', t: 'Just well-drained', d: 'Not sure of texture, but water doesn\'t pool' },
    ];
    return `
      <h3 class="gp-q">What's your soil like?</h3>
      <p class="gp-help">Pick whichever match — you can choose more than one if your spot has different patches. Skip if you're not sure and we'll show plants that handle most soils.</p>
      <div class="gp-cards">
        ${opts.map(o => `
          <button class="gp-card ${a.soil.includes(o.v) ? 'is-active' : ''}" data-toggle="soil:${o.v}">
            <div class="gp-card__t">${o.t}</div>
            <div class="gp-card__d">${o.d}</div>
          </button>
        `).join('')}
      </div>
      ${navRow()}
    `;
  }

  function renderGoals() {
    const a = STATE.answers;
    const opts = [
      { v: 'Privacy/screen', t: 'Privacy / screen' },
      { v: 'Hedge', t: 'Hedge or fence-line' },
      { v: 'Pollinator garden', t: 'Pollinator garden' },
      { v: 'Cut flowers', t: 'Cut flowers' },
      { v: 'Edible/food', t: 'Food / edible' },
      { v: 'Specimen/focal', t: 'Specimen or focal point' },
      { v: 'Erosion control', t: 'Erosion control' },
      { v: 'Groundcover', t: 'Groundcover / weed suppression' },
      { v: 'Container', t: 'Container or patio pots' },
      { v: 'Shade garden', t: 'Shade garden' },
      { v: 'Cottage garden', t: 'Cottage / English garden' },
      { v: 'Rain garden', t: 'Rain garden' },
      { v: 'Border/edging', t: 'Border / edging' },
    ];
    return `
      <h3 class="gp-q">What's the goal for your garden?</h3>
      <p class="gp-help">Pick as many as you'd like — we'll favor plants that fit these uses. Skip if you're just exploring.</p>
      <div class="gp-pills gp-pills--wrap">
        ${opts.map(o => `
          <button class="gp-pill gp-pill--multi ${a.goals.includes(o.v) ? 'is-active' : ''}" data-toggle="goals:${o.v}">${o.t}</button>
        `).join('')}
      </div>
      ${navRow()}
    `;
  }

  function renderPressures() {
    const a = STATE.answers;
    const showNative = a.projectType !== 'vegetable';
    return `
      <h3 class="gp-q">A few local conditions to check.</h3>
      <p class="gp-help">These narrow the list to plants that will actually thrive where you are.</p>

      <div class="gp-subblock">
        <div class="gp-sublabel">Deer pressure on your property?</div>
        <div class="gp-pills">
          <button class="gp-pill ${a.deer === 'high' ? 'is-active' : ''}" data-set="deer:high">Heavy — they eat everything</button>
          <button class="gp-pill ${a.deer === 'low' ? 'is-active' : ''}" data-set="deer:low">Some, but manageable</button>
          <button class="gp-pill ${a.deer === 'none' ? 'is-active' : ''}" data-set="deer:none">No deer here</button>
        </div>
      </div>

      <div class="gp-subblock">
        <div class="gp-sublabel">Are you near the coast or in a windy spot?</div>
        <div class="gp-pills">
          <button class="gp-pill ${a.coastal === true ? 'is-active' : ''}" data-set="coastal:true">Yes — salt spray or strong wind</button>
          <button class="gp-pill ${a.coastal === false ? 'is-active' : ''}" data-set="coastal:false">No — sheltered</button>
        </div>
      </div>

      ${showNative ? `
      <div class="gp-subblock">
        <div class="gp-sublabel">Pacific Northwest natives</div>
        <p class="gp-help" style="margin-top:-6px;margin-bottom:10px">Natives are ecologically superior for local pollinators and birds. We'll lean toward them whenever a native fits your other answers.</p>
        <label class="gp-check">
          <input type="checkbox" id="gp-native" ${a.native ? 'checked' : ''} />
          <span>Yes, lean toward PNW natives when possible</span>
        </label>
      </div>
      ` : ''}

      ${notesSubblockIfLast()}
      ${navRow()}
    `;
  }

  function renderWildlife() {
    const a = STATE.answers;
    const wls = [
      { v: 'Pollinator support', t: 'Bees & pollinators' },
      { v: 'Hummingbird nectar', t: 'Hummingbirds' },
      { v: 'Bird forage', t: 'Songbirds (food)' },
      { v: 'Bird habitat', t: 'Songbirds (shelter)' },
      { v: 'Larval host', t: 'Butterfly host plants' },
      { v: 'Small mammal forage', t: 'Squirrels & small mammals' },
    ];
    return `
      <h3 class="gp-q">Who do you want to invite into your garden?</h3>
      <p class="gp-help">Pick as many as you'd like — we'll favor plants that feed and shelter them.</p>
      <div class="gp-pills gp-pills--wrap">
        ${wls.map(w => `
          <button class="gp-pill gp-pill--multi ${a.wildlife.includes(w.v) ? 'is-active' : ''}" data-toggle="wildlife:${w.v}">${w.t}</button>
        `).join('')}
      </div>
      ${navRow()}
    `;
  }

  function renderAesthetics() {
    const a = STATE.answers;
    const seasons = [
      { v: 'Spring', t: 'Spring' },
      { v: 'Summer', t: 'Summer' },
      { v: 'Fall', t: 'Fall' },
      { v: 'Winter', t: 'Winter' },
      { v: 'Year-round', t: 'Year-round' },
    ];
    return `
      <h3 class="gp-q">Last one — the look you're after.</h3>
      <p class="gp-help">Pick the bloom colors and seasons you'd love to see. Skip if you have no preference.</p>

      <div class="gp-subblock">
        <div class="gp-sublabel">Bloom colors you love</div>
        <div class="gp-pills gp-pills--wrap">
          ${BLOOM_COLORS.map(c => `
            <button class="gp-pill gp-pill--multi gp-pill--swatch" data-toggle="bloomColors:${c}">
              <span class="gp-swatch" style="background:${swatchHex(c)}"></span>
              <span class="${a.bloomColors.includes(c) ? 'is-active-text' : ''}">${c}</span>
              <span class="gp-pill__check">${a.bloomColors.includes(c) ? '✓' : ''}</span>
            </button>
          `).join('')}
        </div>
      </div>

      <div class="gp-subblock">
        <div class="gp-sublabel">When you'd like blooms</div>
        <div class="gp-pills gp-pills--wrap">
          ${seasons.map(s => `
            <button class="gp-pill gp-pill--multi ${a.bloomSeasons.includes(s.v) ? 'is-active' : ''}" data-toggle="bloomSeasons:${s.v}">${s.t}</button>
          `).join('')}
        </div>
      </div>

      ${notesSubblockIfLast()}
      ${navRow()}
    `;
  }

  function swatchHex(c) {
    return ({
      Pink: '#f4a8c0', White: '#fafafa', Purple: '#9b6bbe', Red: '#c64545',
      Yellow: '#f0d24a', Cream: '#f4ecd2', Orange: '#e88a3a', Blue: '#7aa3d4',
      Brown: '#8b6f47', Green: '#7eaa6f',
    })[c] || '#ccc';
  }

  // ---------- Scoring ----------
  function score(p, a) {
    if (!p.z || !p.role) return null;
    // hard filter: zone overlap
    if (a.zone < p.z[0] || a.zone > p.z[1]) return null;
    // hard filter: sun
    if (!sunCompatible(a.sun, p.sun)) return null;
    // hard filter: container size — pots can't hold big plants
    if (a.space === 'container' && p.ht && p.ht[1] > 4) return null;
    // hard filter: coastal exposure
    if (a.coastal === true && p.coast === false) return null;
    // hard filter: drainage — wet sites reject plants that need fast drainage
    if (a.drainage === 'wet' && p.wat === 'low') return null;
    // (slope is now a SOFT preference, applied below — the slope=false metafield
    // covers ~84% of the catalog and likely means "undocumented" more often than
    // "actively slope-intolerant", so a hard filter there over-penalizes)
    // hard filter: soil — only filter when customer specified AND plant has data
    if (a.soil.length && Array.isArray(p.soil) && p.soil.length) {
      const overlap = a.soil.some(s => p.soil.includes(s));
      if (!overlap) return null;
    }

    let s = 0;
    const reasons = [];

    // zone center bonus
    const zCenter = (p.z[0] + p.z[1]) / 2;
    if (Math.abs(zCenter - a.zone) <= 1) s += 1;

    // water
    if (a.water === p.wat) { s += 3; reasons.push('water match'); }
    else if (a.water === 'low' && p.wat === 'high') s -= 8;
    else if (a.water === 'high' && p.wat === 'low') s -= 1;

    // maintenance
    if (a.maintenance && p.maint) {
      if (a.maintenance === p.maint) { s += 3; reasons.push('care match'); }
      else if (a.maintenance === 'low' && p.maint === 'high') s -= 6;
      else if (a.maintenance === 'high' && p.maint === 'low') s += 0; // fine — no penalty
    }

    // garden_use goals
    if (a.goals.length && Array.isArray(p.use) && p.use.length) {
      const matches = a.goals.filter(g => p.use.includes(g));
      if (matches.length) {
        s += matches.length * 4;
        reasons.push(matches.length === 1 ? matches[0].toLowerCase() : `${matches.length} goal matches`);
      }
    }

    // soil bonus when both specified and overlap (we already passed the hard filter)
    if (a.soil.length && Array.isArray(p.soil) && p.soil.length) {
      const matches = a.soil.filter(soil => p.soil.includes(soil));
      if (matches.length >= 2) s += 2; // multi-soil tolerance gets a small boost
    }

    // wildlife
    if (a.wildlife.length) {
      const matches = a.wildlife.filter(w => (p.wv || []).includes(w));
      if (matches.length) {
        s += matches.length * 4;
        reasons.push(matches.length === 1 ? matches[0].toLowerCase() : `${matches.length} wildlife matches`);
      }
    }

    // native
    if (a.native && p.nat === true) { s += 4; reasons.push('PNW native'); }

    // bloom colors
    if (a.bloomColors.length) {
      const matches = a.bloomColors.filter(c => (p.bc || []).includes(c));
      if (matches.length) {
        s += matches.length * 3;
        reasons.push(matches.length === 1 ? `${matches[0].toLowerCase()} blooms` : 'color match');
      }
    }

    // bloom seasons
    if (a.bloomSeasons.length && p.bt) {
      const bt = p.bt.toLowerCase();
      const matches = a.bloomSeasons.filter(season => bt.includes(season.toLowerCase()));
      if (matches.length) {
        s += matches.length * 2;
        reasons.push(`${matches[0].toLowerCase()} bloom`);
      }
    }

    // deer
    if (a.deer === 'high') {
      if (p.deer === true) { s += 6; reasons.push('deer-resistant'); }
      else if (p.deer === false) s -= 12;  // exclude effectively
    } else if (a.deer === 'low' && p.deer === true) {
      s += 2;
    }

    // coastal positive (when user IS coastal)
    if (a.coastal === true && p.coast === true) { s += 2; reasons.push('coastal-tough'); }

    // slope (soft preference): bonus for explicitly slope-tolerant plants,
    // small penalty for slope-intolerant — but not exclusion. The metafield
    // doesn't reliably distinguish "intolerant" from "undocumented".
    if (a.slope === true) {
      if (p.slope === true) { s += 2; reasons.push('slope-tolerant'); }
      else if (p.slope === false) s -= 3;
    }

    // tiebreaker: native +1
    if (p.nat === true && !reasons.includes('PNW native')) s += 0.5;

    return { score: s, reasons };
  }

  function sunCompatible(want, plant) {
    if (!plant) return false;
    // map: want -> set of acceptable plant values
    const map = {
      full: ['full', 'full_part'],
      full_part: ['full', 'full_part', 'part'],
      part: ['full_part', 'part', 'part_shade'],
      part_shade: ['part', 'part_shade', 'shade'],
      shade: ['part_shade', 'shade'],
    };
    return (map[want] || []).includes(plant);
  }

  function computePlan(a, data) {
    // Project type drives output categories. Default to whole_yard if somehow unset.
    const pt = getProjectType(a.projectType) || PROJECT_TYPES[PROJECT_TYPES.length - 1];

    const ranked = [];
    for (const p of data.plants) {
      const r = score(p, a);
      if (r === null) continue;
      ranked.push({ p, score: r.score, reasons: r.reasons });
    }
    ranked.sort((x, y) => y.score - x.score);

    // Per-category cap = base cap × space size multiplier, with hard zeros for
    // tree-class categories in tight spaces.
    const sizeMult = ({ container: 0.5, small: 0.7, medium: 1, large: 1.3 })[a.space] || 1;
    const TREE_CATS = /^(tree|fruit_trees|tall_evergreens|shade_trees|pollinator_trees|accent_trees)$/;
    const buckets = {};
    const meta = {};
    for (const cat of pt.categories) {
      let cap = Math.max(1, Math.round(cat.cap * sizeMult));
      if (a.space === 'container' && TREE_CATS.test(cat.id)) cap = 0;
      if (a.space === 'small' && TREE_CATS.test(cat.id)) cap = Math.min(cap, 1);
      buckets[cat.id] = [];
      meta[cat.id] = { label: cat.label, desc: cat.desc, cap };
    }

    // Each plant goes into at most one category — first match wins, in the
    // project type's declared category order. Two-pass fill: in-stock plants
    // claim slots first; out-of-stock plants only fill remaining capacity so
    // category buckets never come back empty just because Shopify inventory
    // is thin. OOS cards link to the wishlist form on render.
    for (const phase of ['in_stock', 'oos']) {
      for (const r of ranked) {
        const inStock = r.p.inv === true;
        if (phase === 'in_stock' && !inStock) continue;
        if (phase === 'oos' && inStock) continue;
        for (const cat of pt.categories) {
          if (buckets[cat.id].length >= meta[cat.id].cap) continue;
          if (cat.match(r.p, a)) {
            buckets[cat.id].push(r);
            break;
          }
        }
      }
    }

    return {
      total: ranked.length,
      buckets,
      meta,
      projectType: pt,
      answers: a,
    };
  }

  // ---------- Results ----------
  function renderResults() {
    const plan = STATE.plan;
    if (!plan || plan.total === 0) {
      return `
        <div class="gp-empty">
          <h3 class="gp-q">Hmm — we couldn't find matches for that exact combination.</h3>
          <p class="gp-help">Your zone and sun likely narrowed things too tightly. Try loosening one answer (often water or sun) and we'll show more.</p>
          ${btn('Adjust my answers', 'restart', 'primary')}
        </div>
      `;
    }
    const totalShown = Object.values(plan.buckets).reduce((sum, b) => sum + b.length, 0);
    const ptLabel = plan.projectType.label;
    return `
      <div class="gp-results">
        <h3 class="gp-q">Your garden plan</h3>
        <p class="gp-help">${totalShown} plants picked from ${plan.total.toLocaleString()} matches in our nursery, tailored for your <strong>${escapeHtml(ptLabel.toLowerCase())}</strong>.</p>

        <div class="gp-summary">
          ${summaryChip('Project', ptLabel)}
          ${summaryChip('Zone', plan.answers.zone)}
          ${plan.answers.sun ? summaryChip('Sun', sunLabel(plan.answers.sun)) : ''}
          ${plan.answers.water ? summaryChip('Water', cap(plan.answers.water)) : ''}
          ${plan.answers.maintenance ? summaryChip('Care', cap(plan.answers.maintenance)) : ''}
          ${plan.answers.soil.length ? summaryChip('Soil', plan.answers.soil.join(', ')) : ''}
          ${plan.answers.drainage ? summaryChip('Drainage', cap(plan.answers.drainage)) : ''}
          ${plan.answers.deer === 'high' ? summaryChip('Deer', 'Heavy') : ''}
          ${plan.answers.coastal ? summaryChip('Coastal', 'Yes') : ''}
          ${plan.answers.slope === true ? summaryChip('Slope', 'Yes') : ''}
          ${plan.answers.goals.length ? summaryChip('Goals', plan.answers.goals.join(', ')) : ''}
          ${plan.answers.native ? summaryChip('PNW native', 'Preferred') : ''}
        </div>

        <div class="gp-actions">
          <button class="gp-btn gp-btn--primary" data-action="pdf">📄 Download my garden plan PDF</button>
          <button class="gp-btn gp-btn--ghost" data-action="restart">Start over</button>
        </div>

        ${(plan.projectType.id === 'edible' || plan.projectType.id === 'vegetable') ? `
          <div class="gp-disclaimer">
            <strong>A note on edibility:</strong> These plants are tagged as edible based on horticultural sourcing. Always verify with a foraging guide or our nursery staff before consuming any plant. Some "edible" plants only have specific parts that are safe (ripe fruit only, leaves but not seeds, cooked but not raw). When in doubt, ask us.
          </div>
        ` : ''}

        ${plan.projectType.categories.map(cat => {
          const b = plan.buckets[cat.id];
          if (!b || !b.length) return '';
          return `
            <section class="gp-role">
              <h4 class="gp-role__t">${escapeHtml(cat.label)}</h4>
              <p class="gp-role__d">${escapeHtml(cat.desc)}</p>
              <div class="gp-grid">
                ${b.map(({ p, reasons }) => plantCard(p, reasons)).join('')}
              </div>
            </section>
          `;
        }).join('')}

        ${pdfTeaserHtml(plan)}
      </div>
    `;
  }

  // Build contiguous bloom-month spans from a months array. Returns an array
  // of {start, length} runs. For winter blooms ([11, 0, 1]) the result will
  // be two runs: {start: 0, length: 2} and {start: 11, length: 1}.
  function bloomSpans(monthsArr) {
    if (!monthsArr || !monthsArr.length) return [];
    const set = new Set(monthsArr);
    const runs = [];
    let i = 0;
    while (i < 12) {
      if (set.has(i)) {
        let end = i;
        while (end + 1 < 12 && set.has(end + 1)) end++;
        runs.push({ start: i, length: end - i + 1 });
        i = end + 1;
      } else {
        i++;
      }
    }
    return runs;
  }

  function pdfTeaserHtml(plan) {
    const isVeg = plan.projectType.id === 'vegetable';
    const hasNotes = plan.answers.notes && plan.answers.notes.trim();

    const items = [
      ['Year-round bloom calendar', 'A month-by-month gantt chart showing when every plant in your plan is in flower, color-coded by category, with PNW natives marked.'],
      ['3-year phased planting checklist', 'Year 1 structural anchors, Year 2 perennial layer, Year 3 refine and groundcover. Check off plants as you add them.'],
      ['Plants to avoid in coastal Oregon', 'Regional invasives to skip, with what to remove on sight if they volunteer in your yard.'],
    ];

    if (isVeg) {
      const styleLabel = plan.answers.vegStyle === 'raised' ? 'raised beds'
        : plan.answers.vegStyle === 'in_ground' ? 'in-ground beds'
        : 'containers';
      items.push(['Soil prep & amendments', `Tailored to ${styleLabel}. What to fill with, how to amend, when to cover-crop.`]);
    }

    items.push(['Why this plan looks the way it does', 'Citations to Xerces, OSU Extension, Tallamy / Homegrown National Park, NPSO, and Pollinator Partnership.']);

    if (hasNotes) {
      items.push(['Your notes', 'The site description you wrote, captured at the top of the PDF.']);
    }

    return `
      <section class="gp-pdf-teaser">
        <h4 class="gp-pdf-teaser__h">What's in your downloadable plan</h4>
        <p class="gp-pdf-teaser__sub">Your printable PDF goes deeper than what's on this page. Bring it to the nursery, tape it to the fridge, share it with the family.</p>

        <ul class="gp-pdf-teaser__list">
          ${items.map(([title, desc]) => `
            <li class="gp-pdf-teaser__item">
              <span class="gp-pdf-teaser__bullet" aria-hidden="true"></span>
              <div>
                <div class="gp-pdf-teaser__title">${escapeHtml(title)}</div>
                <div class="gp-pdf-teaser__desc">${escapeHtml(desc)}</div>
              </div>
            </li>
          `).join('')}
        </ul>

        <button class="gp-btn gp-btn--primary gp-pdf-teaser__cta" data-action="pdf">📄 Download my garden plan PDF</button>
      </section>
    `;
  }


  function plantCard(p, reasons) {
    const inStock = p.inv === true;
    // OOS plants link to the wishlist form with the plant name + handle prefilled
    // (matches the pattern used by main-product-browse.liquid). Customers stay in
    // the planning flow without hitting a "this isn't on the lot" wall.
    const url = inStock
      ? `${SHOP_DOMAIN}/products/${p.h}`
      : `${SHOP_DOMAIN}/pages/request-a-plant?plant=${encodeURIComponent(p.t)}&handle=${encodeURIComponent(p.h)}`;
    const sizeLine = p.ht && p.wd ? `${formatSize(p.ht)} × ${formatSize(p.wd)}` : '';
    // Always show native badge for native plants — it's a property, not a
    // scoring reason. The reasons pills below are about WHY this plant
    // matched; the native badge is metadata about the plant itself.
    const nativeBadge = p.nat === true ? '<span class="gp-plant__badge">PNW native</span>' : '';
    const stockBadge = inStock
      ? ''
      : '<span class="gp-plant__badge gp-plant__badge--wishlist">Request via wishlist</span>';
    // Filter the existing 'PNW native' reason to avoid duplication with the badge
    const cleanReasons = reasons.filter(r => r !== 'PNW native');
    return `
      <a href="${url}" target="_blank" rel="noopener" class="gp-plant${inStock ? '' : ' gp-plant--oos'}">
        ${p.img ? `<div class="gp-plant__img" style="background-image:url('${p.img}')"></div>` : `<div class="gp-plant__img gp-plant__img--placeholder"></div>`}
        <div class="gp-plant__body">
          <div class="gp-plant__t">${escapeHtml(p.t)}${nativeBadge}${stockBadge}</div>
          ${p.bn ? `<div class="gp-plant__bn"><em>${escapeHtml(p.bn)}</em></div>` : ''}
          ${sizeLine ? `<div class="gp-plant__size">${sizeLine}</div>` : ''}
          ${cleanReasons.length ? `<div class="gp-plant__reasons">${cleanReasons.slice(0, 3).map(r => `<span class="gp-reason">${escapeHtml(r)}</span>`).join('')}</div>` : ''}
        </div>
      </a>
    `;
  }

  function formatSize(arr) {
    if (!arr) return '';
    if (arr[0] === arr[1]) return `${arr[0]} ft`;
    return `${arr[0]}-${arr[1]} ft`;
  }

  function summaryChip(label, val) {
    return `<span class="gp-chip"><strong>${label}:</strong> ${val}</span>`;
  }

  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }
  function sunLabel(s) {
    return ({
      full: 'Full sun', full_part: 'Mostly sun', part: 'Part shade',
      part_shade: 'Mostly shade', shade: 'Full shade',
    })[s] || s;
  }
  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // ---------- PDF helpers ----------
  // Palette used for the bloom calendar and for category color cues. First N
  // categories of the active project type take colors 0..N-1 in order.
  const CALENDAR_PALETTE = [
    [45, 90, 61],     // deep green
    [90, 140, 102],   // mid green
    [201, 123, 157],  // rose
    [217, 160, 74],   // gold
    [122, 163, 212],  // blue
    [155, 107, 190],  // purple
    [200, 100, 80],   // coral
  ];

  // Coarse season-to-month mapping. We don't have per-month bloom data on the
  // catalog (only Spring/Summer/Fall/Winter and a few "early/mid/late"
  // modifiers), so we widen each season to its standard 3-month window. The
  // calendar legend will note this.
  function bloomMonths(bt) {
    if (!bt) return null;
    const s = String(bt).toLowerCase().trim();
    if (!s || s === 'n/a' || s === 'none' || s === 'foliage') return null;
    if (s.includes('year-round') || s.includes('all year') || s.includes('year round')) {
      return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    }
    const m = new Set();
    if (s.includes('spring')) [2, 3, 4].forEach(x => m.add(x));
    if (s.includes('summer')) [5, 6, 7].forEach(x => m.add(x));
    if (s.includes('fall') || s.includes('autumn')) [8, 9, 10].forEach(x => m.add(x));
    if (s.includes('winter')) [11, 0, 1].forEach(x => m.add(x));
    return m.size ? Array.from(m).sort((a, b) => a - b) : null;
  }

  // Maps a category id to a planting phase (1=anchors, 2=perennials, 3=refine).
  function plantPhase(catId) {
    if (/^(tree|fruit_trees|tall_evergreens|tall_accents|shrub|berry_shrubs|anchor_shrubs|mid_screens|screen_filler|shade_shrubs)$/.test(catId)) return 1;
    if (/^(ground|fillers|woodland_ground|ground_filler|edible_ground|meadow_ground|foliage_filler)$/.test(catId)) return 3;
    return 2;
  }

  // Parses a multiline pipe-delimited string into rows. Each row is an array
  // of trimmed cell values. Empty lines are skipped. Returns [] if input is
  // blank, so the PDF can fall back to defaults.
  function parsePipedList(raw) {
    if (!raw || typeof raw !== 'string') return [];
    return raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(line =>
      line.split('|').map(s => s.trim())
    );
  }

  function drawSectionHeader(doc, title, desc, x, y, accentRgb, deepRgb, mutedRgb) {
    doc.setFillColor(accentRgb[0], accentRgb[1], accentRgb[2]);
    doc.rect(x, y - 14, 4, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(deepRgb);
    doc.text(title, x + 12, y);
    if (desc) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(mutedRgb);
      const W = doc.internal.pageSize.getWidth();
      const wrapped = doc.splitTextToSize(desc, W - x * 2 - 12);
      doc.text(wrapped, x + 12, y + 14);
      return y + 14 + wrapped.length * 10 + 6;
    }
    return y + 18;
  }

  // ---------- PDF ----------
  function generatePDF() {
    if (!window.jspdf) {
      alert('PDF tool is still loading — please try again in a moment.');
      return;
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 48; // margin
    let y = M;

    const COLOR_GREEN = '#5d8a3a';   // darker green for print
    const COLOR_DEEP = '#3B2B75';
    const COLOR_BODY = '#2b2b2b';
    const COLOR_MUTED = '#666666';
    const COLOR_CREAM = '#FAF5E8';

    // --- Header ---
    // Cream background band
    doc.setFillColor(COLOR_CREAM);
    doc.rect(0, 0, W, 100, 'F');

    // Logo
    if (LOGO_URL) {
      try {
        doc.addImage(LOGO_URL, 'JPEG', M, 24, 60, 60);
      } catch (e) { /* logo missing — skip */ }
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(COLOR_DEEP);
    doc.text('Your Garden Plan', M + 80, 50);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLOR_MUTED);
    doc.text('Dragonfly Farm & Nursery — Langlois, Oregon', M + 80, 68);

    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(date, W - M, 68, { align: 'right' });

    y = 130;

    // --- Plan summary ---
    const a = STATE.plan.answers;
    const projectType = STATE.plan.projectType;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(COLOR_BODY);
    doc.text('Project:', M, y);
    doc.setFont('helvetica', 'normal');
    doc.text(projectType.label, M + 60, y);
    y += 16;

    doc.setFont('helvetica', 'bold');
    doc.text('Your site:', M, y);
    doc.setFont('helvetica', 'normal');

    const parts = [`Zone ${a.zone}`];
    if (a.sun) parts.push(sunLabel(a.sun));
    if (a.water) parts.push(cap(a.water) + ' water');
    if (a.maintenance) parts.push(cap(a.maintenance) + ' maintenance');
    if (a.soil && a.soil.length) parts.push(a.soil.join('/') + ' soil');
    if (a.drainage) parts.push(cap(a.drainage) + ' drainage');
    if (a.deer === 'high') parts.push('heavy deer');
    if (a.coastal) parts.push('coastal/windy');
    if (a.slope === true) parts.push('on a slope');
    if (a.native) parts.push('PNW natives preferred');
    if (a.goals && a.goals.length) parts.push('for ' + a.goals.slice(0, 3).join(', ').toLowerCase());
    doc.text(parts.join(' · '), M + 60, y);
    y += 24;

    // --- Customer notes (if any) ---
    if (a.notes && a.notes.trim()) {
      const accentRgbForNotes = [93, 138, 58];
      y = drawSectionHeader(doc, 'Your notes', '', M, y, accentRgbForNotes, COLOR_DEEP, COLOR_MUTED);
      y += 4;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(COLOR_BODY);
      const wrapped = doc.splitTextToSize(a.notes.trim(), W - 2 * M);
      for (const line of wrapped) {
        if (y > H - 100) { doc.addPage(); y = M; }
        doc.text(line, M, y);
        y += 12;
      }
      y += 12;
    }

    // --- Plants by category (driven by project type) ---
    const buckets = STATE.plan.buckets;

    for (const cat of projectType.categories) {
      const list = buckets[cat.id];
      if (!list || !list.length) continue;

      if (y > H - 140) { doc.addPage(); y = M; }

      doc.setFillColor(COLOR_GREEN);
      doc.rect(M, y - 14, 4, 18, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(COLOR_DEEP);
      doc.text(cat.label, M + 12, y);
      y += 6;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(COLOR_MUTED);
      doc.text(cat.desc, M + 12, y + 6);
      y += 22;

      for (const { p, reasons } of list) {
        if (y > H - 100) { doc.addPage(); y = M; }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(COLOR_BODY);
        const oosNote = p.inv === true ? '' : '  (request via wishlist)';
        doc.text(`• ${p.t}${oosNote}`, M + 12, y);
        y += 14;

        if (p.bn) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(9);
          doc.setTextColor(COLOR_MUTED);
          doc.text(p.bn, M + 24, y);
          y += 11;
        }

        const meta = [];
        if (p.ht && p.wd) meta.push(`${formatSize(p.ht)} × ${formatSize(p.wd)}`);
        if (p.bt) meta.push(`Blooms: ${p.bt}`);
        if (reasons.length) meta.push(reasons.slice(0, 3).join(', '));
        if (meta.length) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(COLOR_BODY);
          const wrapped = doc.splitTextToSize(meta.join(' · '), W - 2 * M - 24);
          doc.text(wrapped, M + 24, y);
          y += wrapped.length * 11 + 8;
        }
      }
      y += 6;
    }

    // Pull the COLOR_GREEN string into [r,g,b] for the helper. jsPDF accepts
    // hex strings in setFillColor but rect drawing of small swatches reads
    // cleaner with explicit triples. The accent for new sections matches the
    // in-page green bar used above.
    const accentRgb = [93, 138, 58]; // matches COLOR_GREEN '#5d8a3a'

    // --- Bloom Calendar ---
    doc.addPage();
    y = M;
    y = drawSectionHeader(doc, 'Year-Round Bloom Calendar',
      'Each colored bar shows the months a plant is in bloom, color-coded by category. Foliage-only plants carry the in-between weeks.',
      M, y, accentRgb, COLOR_DEEP, COLOR_MUTED);
    y += 6;

    // Build a flat list of plants (with category color and label) in plan order
    const calRows = [];
    projectType.categories.forEach((cat, idx) => {
      const list = buckets[cat.id] || [];
      const color = CALENDAR_PALETTE[idx % CALENDAR_PALETTE.length];
      list.forEach(({ p }) => calRows.push({ p, color, catLabel: cat.label }));
    });

    // Legend: native vs ornamental dots + category swatches
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(COLOR_MUTED);
    let lx = M;
    doc.setFillColor(45, 90, 61); doc.circle(lx + 3, y - 3, 2.2, 'F');
    doc.text('PNW native', lx + 9, y);
    lx += 60;
    doc.setFillColor(217, 200, 155); doc.circle(lx + 3, y - 3, 2.2, 'F');
    doc.text('Ornamental', lx + 9, y);
    lx += 65;
    projectType.categories.forEach((cat, idx) => {
      if (lx > W - M - 80) return; // skip extras if they would overflow
      const c = CALENDAR_PALETTE[idx % CALENDAR_PALETTE.length];
      doc.setFillColor(c[0], c[1], c[2]);
      doc.rect(lx, y - 6, 8, 5, 'F');
      doc.setTextColor(COLOR_BODY);
      doc.text(cat.label, lx + 11, y);
      lx += doc.getTextWidth(cat.label) + 22;
    });
    y += 14;

    // Calendar header row
    const labelW = 200;
    const colW = (W - 2 * M - labelW) / 12;
    const monthLabels = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(COLOR_BODY);
    monthLabels.forEach((m, i) => {
      doc.text(m, M + labelW + i * colW + colW / 2, y, { align: 'center' });
    });
    y += 4;
    doc.setDrawColor(220, 215, 200);
    doc.line(M, y, W - M, y);
    y += 6;

    // Waterfall sort: by first bloom month so the calendar cascades down
    // and to the right. Foliage-only sinks to the bottom.
    calRows.sort((a, b) => {
      const am = bloomMonths(a.p.bt);
      const bm = bloomMonths(b.p.bt);
      const av = (am && am.length) ? am[0] : 99;
      const bv = (bm && bm.length) ? bm[0] : 99;
      return av - bv;
    });

    // Plant rows — render contiguous bloom periods as single bars
    const rowH = 12;
    for (const { p, color } of calRows) {
      if (y > H - 90) { doc.addPage(); y = M; }
      if (p.nat === true) doc.setFillColor(45, 90, 61);
      else doc.setFillColor(217, 200, 155);
      doc.circle(M + 3, y - 3, 2.2, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(COLOR_BODY);
      const nameLines = doc.splitTextToSize(p.t, labelW - 16);
      doc.text(nameLines[0], M + 10, y);
      const bMonths = bloomMonths(p.bt);
      if (bMonths) {
        doc.setFillColor(color[0], color[1], color[2]);
        const runs = bloomSpans(bMonths);
        runs.forEach(run => {
          const x = M + labelW + run.start * colW + 1.5;
          const w = colW * run.length - 3;
          doc.rect(x, y - 7, w, 8, 'F');
        });
      } else {
        doc.setFillColor(232, 230, 222);
        doc.rect(M + labelW, y - 7, colW * 12, 8, 'F');
        doc.setFontSize(7);
        doc.setTextColor(COLOR_MUTED);
        doc.text('foliage / structure', M + labelW + (colW * 12) / 2, y - 1.5, { align: 'center' });
      }
      y += rowH;
    }
    y += 8;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(COLOR_MUTED);
    doc.text('Bloom windows are shown by season (Spring, Summer, Fall, Winter), not by exact month.',
      M, y);
    y += 14;

    // --- Phased Planting ---
    if (y > H - 200) { doc.addPage(); y = M; }
    y = drawSectionHeader(doc, 'Phased Planting',
      'A new garden is a 2 to 3 year project, not a weekend. Front-load the structural plants in fall (when shipping is cheapest and the rains do the watering), then layer in.',
      M, y, accentRgb, COLOR_DEEP, COLOR_MUTED);
    y += 6;

    const phases = [
      { name: 'Phase 1, Foundation', when: 'Fall to Spring', plants: [] },
      { name: 'Phase 2, Layer in', when: 'Spring to Fall', plants: [] },
      { name: 'Phase 3, Refine', when: 'Year 2 onward', plants: [] },
    ];
    projectType.categories.forEach(cat => {
      const phase = plantPhase(cat.id);
      const list = buckets[cat.id] || [];
      list.forEach(({ p }) => phases[phase - 1].plants.push(p.t));
    });
    if (!phases.some(ph => ph.plants.length)) {
      // Defensive fallback: if no plants matched at all (shouldn't happen here)
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(COLOR_MUTED);
      doc.text('No plants in this plan to phase. Adjust your wizard answers and re-download.', M, y);
      y += 12;
    }
    phases.forEach(ph => {
      if (!ph.plants.length) return;
      if (y > H - 100) { doc.addPage(); y = M; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(COLOR_DEEP);
      doc.text(ph.name, M, y);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(COLOR_MUTED);
      doc.text(ph.when, M + 130, y);
      y += 14;

      ph.plants.forEach(name => {
        if (y > H - 80) { doc.addPage(); y = M; }
        doc.setDrawColor(80, 100, 80);
        doc.setLineWidth(0.7);
        doc.rect(M + 4, y - 8, 8, 8);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(COLOR_BODY);
        const nm = doc.splitTextToSize(name, W - 2 * M - 22)[0];
        doc.text(nm, M + 18, y);
        y += 14;
      });
      y += 8;
    });

    // --- Soil prep & amendments (only for vegetable + vegStyle) ---
    if (projectType.id === 'vegetable' && a.vegStyle) {
      if (y > H - 220) { doc.addPage(); y = M; }
      const introCopy = a.vegStyle === 'raised'
        ? 'Raised beds need a structured soil mix that holds moisture, drains well, and rebuilds organic matter every season.'
        : 'In-ground beds reward annual top-dressing with compost, mulch to suppress weeds, and minimum tillage to protect soil life.';
      y = drawSectionHeader(doc, 'Soil prep & amendments', introCopy,
        M, y, accentRgb, COLOR_DEEP, COLOR_MUTED);
      y += 6;

      const tips = a.vegStyle === 'raised' ? [
        ['Initial fill', 'Aim for a mix of 1/3 compost, 1/3 topsoil, 1/3 vermiculite or coarse sand for drainage. New beds typically take 1 to 2 seasons to settle.'],
        ['Annual top-up', 'Add 2 to 3 inches of compost on top each spring. Beds settle 1 to 2 inches per year as organic matter breaks down.'],
        ['Watering', 'Raised beds dry out faster than in-ground. Drip irrigation or hand-watering 3 to 4 times per week in summer is typical.'],
        ['Berries', 'Blueberries need acidic soil (pH 4.5 to 5.5). In raised beds, mix in pine bark fines and a sulfur-based acidifier; test pH yearly.'],
        ['Winter cover', 'Sow crimson clover or winter rye after fall harvest. Chop and turn 2 weeks before spring planting.'],
      ] : [
        ['Initial amendment', 'Spread 2 to 3 inches of compost on the bed surface. Broadfork or shallow-till to incorporate without inverting soil layers.'],
        ['Annual top-dress', 'Each spring, add another 2 inches of compost as mulch around plants. Earthworms work it in for you.'],
        ['No-till maintenance', 'After your first amendment, avoid deep tillage. It destroys soil structure and the fungal network plants depend on.'],
        ['Mulching', 'Straw, shredded leaves, or wood chips on bare soil suppresses weeds and holds moisture. Refresh as it breaks down.'],
        ['Cover crops', 'In late fall, sow crimson clover, winter rye, or vetch on bare beds. Mow or chop and turn 2 weeks before spring planting.'],
        ['pH check', 'Test soil pH every 2 to 3 years. PNW soils trend acidic; lime if growing brassicas. The Curry/Coos extension office runs tests for around $25.'],
      ];

      tips.forEach(([title, body]) => {
        if (y > H - 80) { doc.addPage(); y = M; }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(COLOR_DEEP);
        doc.text(title, M, y);
        y += 12;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(COLOR_BODY);
        const wrapped = doc.splitTextToSize(body, W - 2 * M);
        doc.text(wrapped, M, y);
        y += wrapped.length * 11 + 8;
      });
    }

    // --- Plants to Avoid Here ---
    if (y > H - 220) { doc.addPage(); y = M; }
    y = drawSectionHeader(doc, 'Plants to Avoid in Coastal Oregon',
      'These come up in landscaping but are invasive on the SW Oregon coast. Skip them, and remove on sight if they volunteer.',
      M, y, accentRgb, COLOR_DEEP, COLOR_MUTED);
    y += 6;

    const avoidList = parsePipedList(CFG.avoidListRaw);
    if (!avoidList.length) {
      // Fallback if section setting is blank — keeps the PDF complete on first install
      avoidList.push(
        ['English ivy', 'Hedera helix', 'Smothers native ground layer and climbs trees. Never plant; remove on sight.'],
        ['English holly', 'Ilex aquifolium', 'Invasive on the SW Oregon coast. Birds spread it. Pull volunteers young.'],
        ['Scotch broom', 'Cytisus scoparius', 'Likely volunteering on slopes and disturbed ground. Pull while small.'],
        ['Gorse', 'Ulex europaeus', 'Aggressive coastal invasive. Spiny and very hard to remove once established.'],
        ['Pampas grass', 'Cortaderia selloana', 'Coastal invasive. Use native blue wildrye or tufted hairgrass instead.'],
        ['Japanese knotweed', 'Reynoutria japonica', 'Extremely difficult to eradicate. Report sightings to your local soil and water district.'],
        ['Himalayan blackberry', 'Rubus armeniacus', 'Aggressive thicket-former. Native trailing blackberry is a gentler alternative.']
      );
    }

    avoidList.forEach(([common, latin, why]) => {
      if (y > H - 80) { doc.addPage(); y = M; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(COLOR_BODY);
      doc.text(common, M, y);
      const nameW = doc.getTextWidth(common);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(COLOR_MUTED);
      doc.text(latin, M + nameW + 8, y);
      y += 12;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(COLOR_BODY);
      const wrapped = doc.splitTextToSize(why, W - 2 * M);
      doc.text(wrapped, M, y);
      y += wrapped.length * 11 + 6;
    });

    // (no "Where to source" section — Dragonfly is the assumed first stop)

    // --- Why this plan looks the way it does ---
    if (y > H - 200) { doc.addPage(); y = M; }
    y += 6;
    y = drawSectionHeader(doc, 'Why this plan looks the way it does', '',
      M, y, accentRgb, COLOR_DEEP, COLOR_MUTED);
    y += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLOR_BODY);
    const intro = 'Every plant in this plan is selected by matching your site to authoritative ecological and horticultural sources for the Pacific Northwest. Native specialists need the natives. Generalist bees, butterflies, and hummingbirds work both natives and well-chosen ornamentals.';
    const introWrapped = doc.splitTextToSize(intro, W - 2 * M);
    doc.text(introWrapped, M, y);
    y += introWrapped.length * 12 + 8;

    const cites = parsePipedList(CFG.citationsListRaw);
    if (!cites.length) {
      cites.push(
        ['Xerces Society Maritime Northwest Plant List', 'Invertebrate conservation gold standard. xerces.org'],
        ['OSU Extension', 'Oregon-specific horticultural research. extension.oregonstate.edu'],
        ['Doug Tallamy / Homegrown National Park', 'Native plants as host plants for caterpillars, the foundation of bird food.'],
        ['Native Plant Society of Oregon', 'npsoregon.org'],
        ['Pollinator Partnership ecoregion guide', 'pollinator.org']
      );
    }
    cites.forEach(([name, desc]) => {
      if (y > H - 80) { doc.addPage(); y = M; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(COLOR_DEEP);
      doc.text('• ' + name, M, y);
      y += 12;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(COLOR_MUTED);
      const wrapped = doc.splitTextToSize(desc, W - 2 * M - 12);
      doc.text(wrapped, M + 12, y);
      y += wrapped.length * 10 + 6;
    });

    // --- Footer (every page) ---
    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const fY = H - 60;
      doc.setDrawColor(COLOR_GREEN);
      doc.setLineWidth(1);
      doc.line(M, fY - 14, W - M, fY - 14);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(COLOR_DEEP);
      doc.text('Dragonfly Farm & Nursery', M, fY);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(COLOR_BODY);
      doc.text(CONTACT.address || '49295 Hwy 101, Langlois, OR 97450', M, fY + 12);
      doc.text(`${CONTACT.phone || '541-844-5559'}  ·  ${CONTACT.email || 'contact@dragonflyfarmlanglois.com'}`, M, fY + 24);
      doc.text(CONTACT.hours || 'Open 9am–5pm Daily (seasonal — call to confirm)', M, fY + 36);

      doc.setTextColor(COLOR_MUTED);
      doc.text(CONTACT.website || 'dragonflyfarmlanglois.com', W - M, fY + 12, { align: 'right' });
      doc.text(`Page ${i} of ${pageCount}`, W - M, fY + 36, { align: 'right' });
    }

    doc.save('my-dragonfly-garden-plan.pdf');
  }

  // ---------- Init ----------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
